import { useState, useEffect, useCallback } from "react";
import axios from "../../../configApi/axiosConfig";
import { APIURL } from "../../../configApi/apiConfig";
import { useAuth } from "../../AuthContext/AuthContext";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, IconButton, Switch, TextField, Autocomplete, Chip, InputAdornment } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import TrackChangesIcon from "@mui/icons-material/TrackChanges";

// ── helpers ────────────────────────────────────────────────────────────────
const fmt = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = String(iso).slice(0, 10).split("-");
  return `${d}/${m}/${y}`;
};

const todayISO = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 10);
};

const EMPTY_FORM = {
  Nombre: "",
  Desde: todayISO(),
  Hasta: todayISO(),
  Operadores: "",
  Activo: true,
};

const verificarConflictos = (campanas, idEditando, isActivo, desde, hasta, operadoresString, operadoresLista) => {
  const idsSeleccionados = operadoresString
    ? operadoresString.split(",").map(i => i.trim()).filter(Boolean)
    : [];

  if (!isActivo || idsSeleccionados.length === 0) return [];

  const conflictos = [];
  for (const camp of campanas) {
    if (!camp.Activo) continue;
    if (idEditando && camp.idCbo_CampanaCobranza === idEditando) continue;
    if (!camp.Operadores) continue;

    const idsOtraCamp = camp.Operadores.split(",").map(i => i.trim()).filter(Boolean);
    const desdeB = String(camp.Desde).slice(0, 10);
    const hastaB = String(camp.Hasta).slice(0, 10);
    const seSolapa = desde <= hastaB && hasta >= desdeB;

    if (!seSolapa) continue;

    for (const idOp of idsSeleccionados) {
      if (idsOtraCamp.includes(idOp)) {
        const op = operadoresLista.find(o => String(o.idPersonalBDD) === String(idOp));
        const nombreOp = op ? `${op.primerNombre} ${op.apellidoPaterno}` : `ID ${idOp}`;
        conflictos.push({ operador: nombreOp, campana: camp.Nombre, desde: String(camp.Desde).slice(0,10), hasta: String(camp.Hasta).slice(0,10) });
      }
    }
  }
  return conflictos;
};

// ── componente principal ───────────────────────────────────────────────────
export const CampanaCobranza = () => {
  const { userData } = useAuth();
  const esSupervisor = userData?.idGrupo === 18 || userData?.idGrupo === 1;

  const [campanas, setCampanas] = useState([]);
  const [operadoresLista, setOperadoresLista] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [editando, setEditando] = useState(false);
  const [idEditando, setIdEditando] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [msgExito, setMsgExito] = useState("");
  const [confirmDialog, setConfirmDialog] = useState({ open: false, id: null, nombre: "" });
  const [conflictoDialog, setConflictoDialog] = useState({ open: false, conflictos: [] });
  const [operadoresDialog, setOperadoresDialog] = useState({ open: false, idCampana: null, nombreCampana: "", operadores: "" });
  const [warningDialog, setWarningDialog] = useState({ open: false, mensaje: "" });

  // ── carga de datos ───────────────────────────────────────────────────────
  const fetchCampanas = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(APIURL.get_CampanasCobranza());
      setCampanas(data);
    } catch (e) {
      setError("No se pudieron cargar las campañas.");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchOperadores = useCallback(async () => {
    try {
      const { data } = await axios.get(APIURL.personal_bdd_findAllgestor());
      setOperadoresLista(data || []);
    } catch (e) {
      console.error("Error al cargar operadores", e);
    }
  }, []);

  useEffect(() => {
    fetchCampanas();
    fetchOperadores();
  }, [fetchCampanas, fetchOperadores]);

  // ── acciones de formulario ───────────────────────────────────────────────
  const abrirNueva = () => {
    setEditando(false);
    setIdEditando(null);
    setForm(EMPTY_FORM);
    setModalOpen(true);
  };

  const abrirEditar = (c) => {
    setEditando(true);
    setIdEditando(c.idCbo_CampanaCobranza);
    setForm({
      Nombre: c.Nombre,
      Desde: String(c.Desde).slice(0, 10),
      Hasta: String(c.Hasta).slice(0, 10),
      Operadores: c.Operadores ?? "",
      Activo: c.Activo,
    });
    setModalOpen(true);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const handleGuardar = async () => {
    if (!form.Nombre.trim()) return setWarningDialog({ open: true, mensaje: "El nombre es obligatorio." });
    if (form.Nombre.trim().length < 10) return setWarningDialog({ open: true, mensaje: "El nombre de la campaña debe tener mínimo 10 caracteres." });
    if (form.Desde > form.Hasta) return setWarningDialog({ open: true, mensaje: '"Desde" no puede ser mayor que "Hasta".' });

    const conflictos = verificarConflictos(campanas, idEditando, form.Activo, form.Desde, form.Hasta, form.Operadores, operadoresLista);
    if (conflictos.length > 0) {
      setConflictoDialog({ open: true, conflictos });
      return; 
    }

    setSaving(true);
    try {
      // Agregar T12:00:00 evita que la zona horaria del servidor atrase el día
      const payload = {
        ...form,
        Desde: form.Desde.includes("T") ? form.Desde : form.Desde + "T12:00:00",
        Hasta: form.Hasta.includes("T") ? form.Hasta : form.Hasta + "T12:00:00"
      };

      if (editando) {
        await axios.patch(APIURL.patch_CampanaCobranza(idEditando), payload);
      } else {
        await axios.post(APIURL.post_CampanaCobranza(), payload);
      }
      setModalOpen(false);
      setMsgExito(editando ? "Campaña actualizada ✓" : "Campaña creada ✓");
      setTimeout(() => setMsgExito(""), 3000);
      fetchCampanas();
    } catch (e) {
      setWarningDialog({ open: true, mensaje: "Error al guardar: " + (e?.response?.data?.message || e.message) });
    } finally {
      setSaving(false);
    }
  };

  const handleDesactivarClick = (id, nombre) => {
    setConfirmDialog({ open: true, id, nombre });
  };

  const handleDesactivarConfirm = async () => {
    try {
      await axios.patch(APIURL.patch_DesactivarCampanaCobranza(confirmDialog.id));
      fetchCampanas();
    } catch (e) {
      setWarningDialog({ open: true, mensaje: "Error al desactivar: " + (e?.response?.data?.message || e.message) });
    } finally {
      setConfirmDialog({ open: false, id: null, nombre: "" });
    }
  };

  const abrirGestorOperadores = (c) => {
    setOperadoresDialog({
      open: true,
      idCampana: c.idCbo_CampanaCobranza,
      nombreCampana: c.Nombre,
      operadores: c.Operadores || ""
    });
  };

  const handleGuardarOperadores = async () => {
    const idsSeleccionados = operadoresDialog.operadores
      ? operadoresDialog.operadores.split(",").map(i => i.trim()).filter(Boolean)
      : [];

    const campActual = campanas.find(c => c.idCbo_CampanaCobranza === operadoresDialog.idCampana);
    const conflictos = verificarConflictos(
      campanas, 
      operadoresDialog.idCampana, 
      campActual?.Activo, 
      campActual ? String(campActual.Desde).slice(0, 10) : "", 
      campActual ? String(campActual.Hasta).slice(0, 10) : "", 
      operadoresDialog.operadores, 
      operadoresLista
    );

    if (conflictos.length > 0) {
      setConflictoDialog({ open: true, conflictos });
      return; 
    }

    setSaving(true);
    try {
      await axios.patch(APIURL.patch_CampanaCobranza(operadoresDialog.idCampana), { Operadores: operadoresDialog.operadores });
      setMsgExito("Operadores actualizados ✓");
      setTimeout(() => setMsgExito(""), 3000);
      setOperadoresDialog({ open: false, idCampana: null, nombreCampana: "", operadores: "" });
      fetchCampanas();
    } catch(e) {
      setWarningDialog({ open: true, mensaje: "Error: " + (e?.response?.data?.message || e.message) });
    } finally {
      setSaving(false);
    }
  };

  // ── render ───────────────────────────────────────────────────────────────
  const hoy = todayISO();

  return (
    <div style={{ padding: "32px 40px", backgroundColor: "#f8fafc", minHeight: "100vh", fontFamily: "'Inter', 'Roboto', sans-serif" }}>

      {/* Título de la vista */}
      <div style={{ marginBottom: 32 }}>
        <h2 style={{ fontSize: 24, fontWeight: 700, color: "#1e293b", margin: "0 0 8px 0" }}>Gestión de Campañas de Cobranza</h2>
        <p style={{ fontSize: 14, color: "#64748b", margin: 0 }}>Crea y administra campañas específicas para tus carteras de clientes</p>
      </div>

      {/* Contenedor tipo Tarjeta */}
      <div style={{
        backgroundColor: "#ffffff",
        borderRadius: 12,
        boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -2px rgba(0, 0, 0, 0.05)",
        border: "1px solid #f1f5f9",
        padding: "24px 32px",
        marginBottom: 24
      }}>

        {/* header de la tarjeta */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
          <div>
            <p style={{ fontSize: 18, fontWeight: 700, color: "#1e293b", margin: "0 0 6px 0", display: "flex", alignItems: "center", gap: 8 }}>
              🎯 Campañas de Cobranza
            </p>
            <p style={{ fontSize: 13, color: "#64748b", margin: 0 }}>
              Una campaña activa permite buscar en TODA la cartera del rango seleccionado.
            </p>
          </div>
          {esSupervisor && (
            <button
              onClick={abrirNueva}
              style={{
                background: "#f97316", color: "#fff", border: "none",
                borderRadius: 8, padding: "10px 20px", fontSize: 14,
                fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 6,
                transition: "background 0.2s"
              }}
              onMouseOver={(e) => (e.currentTarget.style.background = "#ea6c0a")}
              onMouseOut={(e) => (e.currentTarget.style.background = "#f97316")}
            >
              <span style={{ fontSize: 16, lineHeight: 1 }}>+</span> Nueva Campaña
            </button>
          )}
        </div>

        {/* mensaje éxito */}
        {msgExito && (
          <div style={{
            background: "#dcfce7", border: "1px solid #bbf7d0", color: "#166534",
            borderRadius: 8, padding: "12px 16px", marginBottom: 20, fontSize: 14, fontWeight: 500,
          }}>
            {msgExito}
          </div>
        )}

        {/* estados */}
        {loading && <p style={{ color: "#64748b", fontSize: 14 }}>Cargando campañas...</p>}
        {error && <p style={{ color: "#ef4444", fontSize: 14 }}>{error}</p>}

        {/* tabla */}
        {!loading && !error && (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
              <thead>
                <tr style={{ background: "#f8fafc" }}>
                  {["Nombre", "Desde", "Hasta", "Operadores", "Estado", "Vigencia", "Creado por", "Acciones"].map((h, i, arr) => (
                    <th key={h} style={{
                      textAlign: "left", padding: "12px 16px",
                      color: "#64748b", borderTop: "1px solid #f1f5f9", borderBottom: "1px solid #f1f5f9",
                      fontSize: 12, fontWeight: 600, textTransform: "uppercase",
                      letterSpacing: "0.05em", whiteSpace: "nowrap",
                      ...(i === 0 ? { borderTopLeftRadius: 8, borderBottomLeftRadius: 8, borderLeft: "1px solid #f1f5f9" } : {}),
                      ...(i === arr.length - 1 ? { borderTopRightRadius: 8, borderBottomRightRadius: 8, borderRight: "1px solid #f1f5f9" } : {})
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {campanas.length === 0 && (
                  <tr>
                    <td colSpan={8} style={{ textAlign: "center", padding: "48px 0", color: "#94a3b8", fontSize: 14 }}>
                      No hay campañas registradas aún.
                    </td>
                  </tr>
                )}
                {campanas.map((c) => {
                  const activa = c.Activo &&
                    String(c.Desde).slice(0, 10) <= hoy &&
                    String(c.Hasta).slice(0, 10) >= hoy;
                  return (
                    <tr key={c.idCbo_CampanaCobranza}
                      style={{ borderBottom: "1px solid #f1f5f9", transition: "background 0.2s" }}
                      onMouseOver={(e) => (e.currentTarget.style.background = "#f8fafc")}
                      onMouseOut={(e) => (e.currentTarget.style.background = "transparent")}
                    >
                      <td style={{ padding: "16px", fontWeight: 500, color: "#334155" }}>{c.Nombre}</td>
                      <td style={{ padding: "16px", color: "#475569" }}>{fmt(c.Desde)}</td>
                      <td style={{ padding: "16px", color: "#475569" }}>{fmt(c.Hasta)}</td>
                      <td style={{ padding: "16px", color: "#475569" }}>
                        {!c.Operadores ? (
                          <span style={{ color: "#94a3b8", fontSize: 13 }}>Todos (Global)</span>
                        ) : (
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <span style={{ fontSize: 13, fontWeight: 500 }}>
                              {c.Operadores.split(",").filter(Boolean).length} Asignados
                            </span>
                            <button
                              onClick={() => abrirGestorOperadores(c)}
                              style={{
                                background: "#e0e7ff", color: "#4f46e5", border: "none",
                                borderRadius: 6, padding: "4px 8px", fontSize: 12,
                                fontWeight: 600, cursor: "pointer", transition: "background 0.2s"
                              }}
                              onMouseOver={(e) => (e.currentTarget.style.background = "#c7d2fe")}
                              onMouseOut={(e) => (e.currentTarget.style.background = "#e0e7ff")}
                              title="Ver operadores"
                            >
                              👁️ Ver
                            </button>
                          </div>
                        )}
                      </td>
                      <td style={{ padding: "16px" }}>
                        <span style={{
                          display: "inline-flex", alignItems: "center", gap: 4, padding: "4px 10px", borderRadius: 999, fontSize: 12,
                          fontWeight: 600,
                          background: c.Activo ? "#dcfce7" : "#fee2e2",
                          color: c.Activo ? "#166534" : "#991b1b",
                        }}>
                          {c.Activo ? <CheckCircleIcon sx={{ fontSize: 14 }} /> : null}
                          {c.Activo ? "Activo" : "Inactive"}
                        </span>
                      </td>
                      <td style={{ padding: "16px" }}>
                        <span style={{
                          display: "inline-block", padding: "4px 10px", borderRadius: 999, fontSize: 12,
                          fontWeight: 600,
                          background: activa ? "#ffedd5" : "#f1f5f9",
                          color: activa ? "#9a3412" : "#64748b",
                        }}>
                          {activa ? "Vigente hoy" : "Fuera de rango"}
                        </span>
                      </td>
                      <td style={{ padding: "16px", color: "#64748b" }}>{c.CreadoPor ?? "—"}</td>
                      <td style={{ padding: "16px" }}>
                        {esSupervisor && (
                          <div style={{ display: "flex", gap: 16 }}>
                            <button
                              onClick={() => abrirEditar(c)}
                              style={{
                                display: "flex", alignItems: "center", gap: 6,
                                padding: 0, fontSize: 13, fontWeight: 500,
                                border: "none", color: "#475569", background: "transparent",
                                cursor: "pointer", transition: "color 0.2s"
                              }}
                              onMouseOver={(e) => { e.currentTarget.style.color = "#0f172a"; }}
                              onMouseOut={(e) => { e.currentTarget.style.color = "#475569"; }}
                            >
                              <EditOutlinedIcon sx={{ fontSize: 16 }} /> Editar
                            </button>
                            {c.Activo && (
                              <button
                                onClick={() => handleDesactivarClick(c.idCbo_CampanaCobranza, c.Nombre)}
                                style={{
                                  display: "flex", alignItems: "center", gap: 6,
                                  padding: 0, fontSize: 13, fontWeight: 500,
                                  border: "none", color: "#f59e0b", background: "transparent",
                                  cursor: "pointer", transition: "color 0.2s"
                                }}
                                onMouseOver={(e) => { e.currentTarget.style.color = "#d97706"; }}
                                onMouseOut={(e) => { e.currentTarget.style.color = "#f59e0b"; }}
                              >
                                <PauseCircleOutlineIcon sx={{ fontSize: 16 }} /> Desactivar
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Paginación falsa visual (mockup) */}
            {campanas.length > 0 && (
              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 24, gap: 8 }}>
                <button style={{ width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center", background: "#fff", border: "1px solid #e2e8f0", borderRadius: 6, color: "#cbd5e1", cursor: "not-allowed" }}>&lt;</button>
                <button style={{ width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center", background: "#f1f5f9", border: "1px solid #e2e8f0", borderRadius: 6, color: "#334155", fontWeight: 600 }}>1</button>
                <button style={{ width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center", background: "#fff", border: "1px solid #e2e8f0", borderRadius: 6, color: "#cbd5e1", cursor: "not-allowed" }}>&gt;</button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* aviso no supervisor */}
      {!esSupervisor && (
        <div style={{
          marginTop: 24, padding: "12px 18px", borderRadius: 10,
          background: "#fef3c7", border: "1px solid #fcd34d", color: "#92400e",
          fontSize: 13,
        }}>
          👁 Solo los <strong>supervisores</strong> pueden crear o modificar campañas.
          Aquí puedes ver las campañas activas que aplican a tu cartera.
        </div>
      )}

      {/* ── modal ──────────────────────────────────────────────────────────── */}
      <Dialog 
        open={modalOpen} 
        onClose={() => setModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ style: { borderRadius: 16 } }}
      >
        <DialogTitle sx={{ m: 0, p: 3, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid #f1f5f9" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <TrackChangesIcon sx={{ color: "#475569", fontSize: 28 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#1e293b", fontSize: 20 }}>
              {editando ? "Editar Campaña de Cobranza" : "Nueva Campaña de Cobranza"}
            </Typography>
          </div>
          <IconButton onClick={() => setModalOpen(false)} sx={{ color: "#94a3b8" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ p: 0 }}>
          <div style={{ padding: "24px 32px", display: "flex", flexDirection: "column", gap: 28 }}>
            
            {/* Sección 1 */}
            <div>
              <Typography sx={{ fontWeight: 600, color: "#475569", mb: 2, fontSize: 15 }}>Datos Generales</Typography>
              <div style={{ marginBottom: 16 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#1e293b", mb: 1 }}>
                  Nombre de Campaña<span style={{ color: "#ef4444" }}>*</span>
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  name="Nombre"
                  value={form.Nombre}
                  onChange={handleChange}
                  placeholder="Ej: Campaña de Cobranza de Octubre 2026"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </div>
              
              <div>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#1e293b", mb: 1 }}>
                  Período de Vigencia<span style={{ color: "#ef4444" }}>*</span>
                </Typography>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                  <TextField
                    type="date"
                    size="small"
                    name="Desde"
                    value={form.Desde}
                    onChange={handleChange}
                    InputProps={{ startAdornment: <Typography sx={{ color: "#94a3b8", mr: 1, fontSize: 14 }}>Desde</Typography> }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                  <TextField
                    type="date"
                    size="small"
                    name="Hasta"
                    value={form.Hasta}
                    onChange={handleChange}
                    InputProps={{ startAdornment: <Typography sx={{ color: "#94a3b8", mr: 1, fontSize: 14 }}>Hasta</Typography> }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </div>
              </div>
            </div>

            {/* Sección 2 */}
            <div>
              <Typography sx={{ fontWeight: 600, color: "#475569", mb: 2, fontSize: 15 }}>Asignación de Operadores</Typography>
              <div>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#1e293b", mb: 1 }}>
                  Operadores Habilitados
                </Typography>
                
                <Autocomplete
                  multiple
                  options={operadoresLista}
                  getOptionLabel={(option) => `${option.primerNombre} ${option.apellidoPaterno}`}
                  isOptionEqualToValue={(option, value) => String(option.idPersonalBDD) === String(value.idPersonalBDD)}
                  value={
                    (form.Operadores ? form.Operadores.split(",").map(i => i.trim()).filter(Boolean) : [])
                      .map(id => operadoresLista.find(o => String(o.idPersonalBDD) === String(id)))
                      .filter(Boolean)
                  }
                  onChange={(event, newValue) => {
                    const ids = newValue.map(item => item.idPersonalBDD).join(",");
                    handleChange({ target: { name: "Operadores", value: ids } });
                  }}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip 
                        variant="filled"
                        label={`${option.primerNombre} ${option.apellidoPaterno}`.toUpperCase()} 
                        {...getTagProps({ index })} 
                        sx={{ background: "#f1f5f9", color: "#334155", fontWeight: 600, borderRadius: 2 }}
                        deleteIcon={<CloseIcon sx={{ color: "#94a3b8" }} />}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField 
                      {...params} 
                      size="small"
                      placeholder={form.Operadores ? "" : "Buscar operadores..."}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, py: '4px' } }}
                    />
                  )}
                />
                <Typography sx={{ fontSize: 12, color: "#94a3b8", mt: 1 }}>
                  Dejar vacío para que aplique a todos
                </Typography>
              </div>
            </div>

            {/* Sección 3 */}
            <div>
              <Typography sx={{ fontWeight: 600, color: "#475569", mb: 2, fontSize: 15 }}>Opciones</Typography>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <Switch 
                  checked={form.Activo}
                  onChange={(e) => handleChange({ target: { name: "Activo", type: "checkbox", checked: e.target.checked } })}
                  sx={{ 
                    '& .MuiSwitch-switchBase.Mui-checked': { color: "#f97316" },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: "#f97316" }
                  }}
                />
                <Typography sx={{ fontSize: 14, color: "#1e293b", fontWeight: 500 }}>
                  Campaña activa
                </Typography>
              </div>
              <Typography sx={{ fontSize: 12, color: "#64748b", mt: 1, ml: "64px" }}>
                Nota: Las campañas inactivas no se podrán utilizar.
              </Typography>
            </div>
            
          </div>
        </DialogContent>
        
        <DialogActions sx={{ p: "16px 32px 24px", borderTop: "1px solid #f1f5f9", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography sx={{ fontSize: 12, color: "#64748b" }}>
            Los campos marcados con <span style={{ color: "#ef4444" }}>*</span> son obligatorios
          </Typography>
          <div style={{ display: "flex", gap: 12 }}>
            <Button 
              onClick={() => setModalOpen(false)}
              sx={{ color: "#475569", border: "1px solid #cbd5e1", borderRadius: 2, px: 3, textTransform: "none", fontWeight: 600 }}
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleGuardar}
              disabled={saving}
              variant="contained"
              sx={{ 
                background: "#f97316", color: "#fff", borderRadius: 2, px: 4, textTransform: "none", fontWeight: 600, boxShadow: "none",
                '&:hover': { background: "#ea6c0a", boxShadow: "none" },
                '&.Mui-disabled': { background: "#cbd5e1", color: "#fff" }
              }}
            >
              {saving ? "Guardando..." : "Guardar"}
            </Button>
          </div>
        </DialogActions>
      </Dialog>

      {/* Confirmación Modal Desactivar */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, id: null, nombre: "" })}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          style: { borderRadius: 16, padding: "8px 4px" }
        }}
      >
        <DialogTitle sx={{ m: 0, p: 2, display: "flex", alignItems: "center", gap: 1 }}>
          <WarningAmberIcon sx={{ color: "#f59e0b" }} />
          <Typography variant="h6" component="span" sx={{ fontWeight: 600, fontSize: 18 }}>
            Confirmar Acción
          </Typography>
          <IconButton
            onClick={() => setConfirmDialog({ open: false, id: null, nombre: "" })}
            sx={{ position: 'absolute', right: 12, top: 12, color: (theme) => theme.palette.grey[500] }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 3, pb: 2 }}>
          <Typography sx={{ color: "#4b5563", fontSize: 14 }}>
            ¿Estás seguro de que deseas desactivar la campaña <strong>"{confirmDialog.nombre}"</strong>?
          </Typography>
          <Typography sx={{ color: "#6b7280", fontSize: 13, mt: 1 }}>
            Esta acción detendrá las búsquedas globales de esta campaña inmediatamente.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={() => setConfirmDialog({ open: false, id: null, nombre: "" })}
            sx={{ color: "#6b7280", textTransform: "none", fontWeight: 600, borderRadius: 2 }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleDesactivarConfirm}
            variant="contained"
            disableElevation
            sx={{
              backgroundColor: "#ef4444",
              color: "#fff",
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 2,
              '&:hover': { backgroundColor: "#dc2626" }
            }}
          >
            Desactivar
          </Button>
        </DialogActions>
      </Dialog>


      {/* Modal de Conflicto de Operadores */}
      <Dialog
        open={conflictoDialog.open}
        onClose={() => setConflictoDialog({ open: false, conflictos: [] })}
        maxWidth="sm"
        fullWidth
        PaperProps={{ style: { borderRadius: 16 } }}
      >
        <DialogTitle sx={{ m: 0, p: 3, display: "flex", alignItems: "center", gap: 1, borderBottom: "1px solid #f1f5f9" }}>
          <WarningAmberIcon sx={{ color: "#f59e0b", fontSize: 28 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#1e293b", fontSize: 18 }}>
            Conflicto de Operadores Detectado
          </Typography>
          <IconButton
            onClick={() => setConflictoDialog({ open: false, conflictos: [] })}
            sx={{ position: 'absolute', right: 12, top: 12, color: (theme) => theme.palette.grey[500] }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 3, py: 2 }}>
          <Typography sx={{ fontSize: 14, color: "#475569", mb: 2 }}>
            Los siguientes operadores ya tienen una campaña activa que se <strong>solapa con las fechas seleccionadas</strong>:
          </Typography>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {conflictoDialog.conflictos.map((c, i) => (
              <div key={i} style={{
                background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: 10,
                padding: "12px 16px"
              }}>
                <Typography sx={{ fontWeight: 700, color: "#c2410c", fontSize: 14 }}>
                  👤 {c.operador}
                </Typography>
                <Typography sx={{ fontSize: 13, color: "#7c3aed", mt: 0.5 }}>
                  Ya está en la campaña: <strong>"{c.campana}"</strong>
                </Typography>
                <Typography sx={{ fontSize: 12, color: "#64748b", mt: 0.5 }}>
                  Fechas: {fmt(c.desde)} → {fmt(c.hasta)}
                </Typography>
              </div>
            ))}
          </div>
          <Typography sx={{ fontSize: 13, color: "#64748b", mt: 2 }}>
            Por favor, revisa y retira esos operadores antes de guardar, o cambia las fechas de la campaña.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() => setConflictoDialog({ open: false, conflictos: [] })}
            variant="contained"
            disableElevation
            sx={{
              background: "#f97316", color: "#fff", textTransform: "none",
              fontWeight: 600, borderRadius: 2,
              '&:hover': { background: "#ea6c0a" }
            }}
          >
            Entendido, corregir
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal Gestor de Operadores */}
      <Dialog
        open={operadoresDialog.open}
        onClose={() => setOperadoresDialog({ open: false, idCampana: null, nombreCampana: "", operadores: "" })}
        maxWidth="sm"
        fullWidth
        PaperProps={{ style: { borderRadius: 16 } }}
      >
        <DialogTitle sx={{ m: 0, p: 3, display: "flex", alignItems: "center", gap: 1, borderBottom: "1px solid #f1f5f9" }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#1e293b", fontSize: 18 }}>
            Operadores de: {operadoresDialog.nombreCampana}
          </Typography>
          <IconButton
            onClick={() => setOperadoresDialog({ open: false, idCampana: null, nombreCampana: "", operadores: "" })}
            sx={{ position: 'absolute', right: 12, top: 12, color: (theme) => theme.palette.grey[500] }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 3, py: 3 }}>
          <Typography sx={{ fontSize: 13, color: "#64748b", mb: 2 }}>
            Agrega o quita operadores para esta campaña. Deja vacío si quieres que aplique a todos.
          </Typography>
          
          <Autocomplete
            multiple
            options={operadoresLista}
            getOptionLabel={(option) => `${option.primerNombre} ${option.apellidoPaterno}`}
            value={
              operadoresDialog.operadores
                ? operadoresDialog.operadores
                    .split(",")
                    .map(i => i.trim())
                    .filter(Boolean)
                    .map(id => operadoresLista.find(o => String(o.idPersonalBDD) === String(id)))
                    .filter(Boolean)
                : []
            }
            onChange={(_, newValue) => {
              const ids = newValue.map(v => v.idPersonalBDD).join(",");
              setOperadoresDialog({ ...operadoresDialog, operadores: ids });
            }}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  variant="outlined"
                  label={`${option.primerNombre} ${option.apellidoPaterno}`}
                  {...getTagProps({ index })}
                  sx={{ borderRadius: 1, height: 26, fontSize: 12, fontWeight: 500 }}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                placeholder="Buscar operador..."
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13, backgroundColor: "#fff" }
                }}
              />
            )}
          />

        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() => setOperadoresDialog({ open: false, idCampana: null, nombreCampana: "", operadores: "" })}
            sx={{ color: "#6b7280", textTransform: "none", fontWeight: 600, borderRadius: 2 }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleGuardarOperadores}
            disabled={saving}
            variant="contained"
            disableElevation
            sx={{
              background: "#4f46e5", color: "#fff", textTransform: "none",
              fontWeight: 600, borderRadius: 2,
              '&:hover': { background: "#4338ca" },
              '&.Mui-disabled': { background: "#c7d2fe", color: "#fff" }
            }}
          >
            {saving ? "Guardando..." : "Guardar Operadores"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal Genérico de Advertencias y Errores */}
      <Dialog
        open={warningDialog.open}
        onClose={() => setWarningDialog({ open: false, mensaje: "" })}
        maxWidth="xs"
        fullWidth
        PaperProps={{ style: { borderRadius: 16 } }}
      >
        <DialogTitle sx={{ m: 0, p: 3, pb: 2, display: "flex", alignItems: "center", gap: 1 }}>
          <WarningAmberIcon sx={{ color: "#ef4444", fontSize: 28 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#1e293b", fontSize: 18 }}>
            Atención
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ px: 3, pb: 2 }}>
          <Typography sx={{ fontSize: 14, color: "#475569" }}>
            {warningDialog.mensaje}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() => setWarningDialog({ open: false, mensaje: "" })}
            variant="contained"
            disableElevation
            sx={{
              background: "#1e293b", color: "#fff", textTransform: "none",
              fontWeight: 600, borderRadius: 2, px: 4,
              '&:hover': { background: "#0f172a" }
            }}
          >
            Aceptar
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

// ── estilos inline reutilizables ──────────────────────────────────────────────────
const inputStyle = {
  width: "100%", boxSizing: "border-box",
  border: "1px solid #d1d5db", borderRadius: 8,
  padding: "9px 12px", fontSize: 13, color: "#111827",
  outline: "none",
};

const labelStyle = {
  fontSize: 12, fontWeight: 600, color: "#374151",
  display: "block", marginBottom: 4,
};
